import { inject, Injectable } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { ComponentStore, tapResponse } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import { tuiIsPresent } from '@taiga-ui/cdk';
import { ENVIRONMENT } from '@us/app-config';
import { Category, CategoryWithoutItems, NonNullDynamicState, SelectorUtil } from '@us/web/shared/data-access';
import { getErrorMessage, getSearchableText, isFnBApp } from '@us/web/shared/utils';
import { selectShopId, selectTurnOnTheStoreChainLinkFeature } from '@us/web/shop/data-access';
import { selectSelectedShopId } from '@us/web/shops/data-access';
import { pick } from 'lodash';
import {
    combineLatestWith,
    distinctUntilChanged,
    filter,
    Observable,
    share,
    shareReplay,
    startWith,
    switchMap,
    tap
} from 'rxjs';
import { CategoriesService } from '../services/categories.service';
import { selectUser } from '@us/web/auth/data-access';

interface CategoriesState extends NonNullDynamicState<'categories', Category[]> {
    selectedCategory: CategoryWithoutItems | null;
    searchInputFocused: boolean;
    searchString: string;
}

const initialState: CategoriesState = {
    categories: [],
    selectedCategory: null,
    searchInputFocused: false,
    searchString: '',
    status: 'pending',
    error: null
};

@Injectable()
export class CategoriesStore extends ComponentStore<CategoriesState> {
    private readonly store = inject(Store);
    private readonly categoriesService = inject(CategoriesService);
    private readonly router = inject(Router);
    private readonly env = inject(ENVIRONMENT);

    readonly shopIdParams$ =
        // TODO: handle selectTurnOnTheStoreChainLinkFeature for FnB
        (isFnBApp(this.env)
            ? this.store.select(selectShopId)
            : this.store
                  .select(selectTurnOnTheStoreChainLinkFeature)
                  .pipe(
                      switchMap(chain =>
                          chain ? this.store.select(selectSelectedShopId) : this.store.select(selectShopId)
                      )
                  )
        ).pipe(filter(tuiIsPresent), distinctUntilChanged());

    readonly categories$ = this.shopIdParams$.pipe(
        combineLatestWith(this.store.select(selectUser)),
        tap(([shopId, user]) => {
            const customer_phone = user ? user.phone : null;
            this.loadCategories({ shopId, customer_phone });
        }),
        shareReplay(1),
        switchMap(() => this.select(s => s.categories))
    );

    readonly isLoading$ = this.select(SelectorUtil.isLoading);
    readonly searchString$ = this.select(state => state.searchString);

    readonly categoriesWithoutItems$ = this.select(this.categories$, categories =>
        categories.map(c => pick(c, ['id', 'name']) as CategoryWithoutItems)
    );

    private readonly navigationStart$ = this.router.events.pipe(filter(e => e instanceof NavigationStart));

    readonly selectedCategory$ = this.navigationStart$.pipe(
        startWith(null),
        tap(() => {
            this.patchState({
                selectedCategory: null
            });
        }),
        switchMap(() => this.select(state => state.selectedCategory))
    );

    readonly searchInputFocused$ = this.select(state => state.searchInputFocused);
    readonly allItems$ = this.select(this.categories$, categories => categories.flatMap(c => c.items));
    readonly totalItems$ = this.select(this.allItems$, items => items.length);

    readonly filteredCategories$ = this.select(this.categories$, this.searchString$, (categories, searchString) => {
        if (searchString === '' || searchString === null) {
            return categories.slice();
        }
        const str = getSearchableText(searchString).toLowerCase();
        return categories.reduce((acc: Category[], category) => {
            const filtered: Category = {
                ...category,
                items: category.items.filter(item => item.search_str.includes(str))
            };

            if (filtered.items.length > 0) {
                acc.push(filtered);
            }

            return acc;
        }, []);
    });

    readonly loadCategories = this.effect((params$: Observable<{ shopId: string; customer_phone: string | null }>) =>
        params$.pipe(
            tap(() => {
                this.patchState({
                    status: 'loading',
                    error: null
                });
            }),
            switchMap(({ shopId, customer_phone }) =>
                this.categoriesService.getFoodMenu(shopId, customer_phone).pipe(
                    tapResponse(
                        categories => {
                            this.patchState({
                                categories,
                                // selectedCategory: (pick(arrFirst(categories), ['id', 'name']) ||
                                //     null) as CategoryWithoutItems | null,
                                status: 'success',
                                error: ''
                            });
                        },
                        error => {
                            this.patchState({
                                status: 'error',
                                error: getErrorMessage(error)
                            });
                        }
                    )
                )
            )
        )
    );

    readonly selectCategory = this.updater(
        (state, selectedCategory: CategoryWithoutItems): CategoriesState => ({
            ...state,
            selectedCategory
        })
    );

    readonly focusSearchInput = this.updater(
        (state, searchInputFocused: boolean): CategoriesState => ({
            ...state,
            searchInputFocused
        })
    );

    constructor() {
        super({ ...initialState });
    }

    readonly item$ = (id: number) => this.select(this.allItems$, items => items.find(i => i.id === id));
}
